import { c as _c } from "react/compiler-runtime";
import { BadgeList } from '@/components/Atoms/BadgeList/BadgeList';
import { Hidden } from '@/components/Atoms/Hidden/Hidden';
import { Icon, IconSize } from '@/components/Atoms/Icon/Icon';
import Calendar from '@/components/Icons/Calendar';
import { Card, CardActions, CardBody } from '@/components/Molecules/Card/Card';
import { ContentTypeHeader } from '@/components/Molecules/ContentTypeHeader/ContentTypeHeader';
import { EventLiveIndicator } from '@/components/Molecules/EventLiveIndicator/EventLiveIndicator';
import { EventMetadata, StyledMetadataList } from '@/components/Molecules/EventMetadata/EventMetadata';
import { SubscribeButton } from '@/components/Molecules/SubscribeButton/SubscribeButton';
import { EventCTA } from '@/components/Organisms/EventCTA/EventCTA';
import useTranslation from '@/hooks/useTranslation';
import type { IEvent, IEventPublication } from '@/interfaces';
import { ContentType } from '@/interfaces';
import { getMonthAndYearStringForLocale } from '@/utils/dateUtils';
import type { ComponentProps, CSSProperties, ElementType } from 'react';
import { useId } from 'react';
import type { FCl } from 'react-fc';
interface IProps extends Pick<ComponentProps<typeof Card>, 'className' | 'id' | 'href' | 'boxed'> {
  event: IEvent;
  initialHeadingLevel?: number;
}
export const ScheduledEvent: FCl<IProps> = t0 => {
  const $ = _c(34);
  let className;
  let event;
  let rest;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      event,
      initialHeadingLevel: t1,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = event;
    $[3] = rest;
    $[4] = t1;
  } else {
    className = $[1];
    event = $[2];
    rest = $[3];
    t1 = $[4];
  }
  const initialHeadingLevel = t1 === undefined ? 3 : t1;
  const titleId = useId();
  if (event.type === ContentType.publication) {
    let t2;
    if ($[5] !== className || $[6] !== event || $[7] !== initialHeadingLevel || $[8] !== rest) {
      t2 = <PublicationEvent className={className} event={event} initialHeadingLevel={initialHeadingLevel} {...rest} />;
      $[5] = className;
      $[6] = event;
      $[7] = initialHeadingLevel;
      $[8] = rest;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let title = event.title;
  if (event.type === ContentType.summit && event.location) {
    title = `${title} ${event.location}`;
  }
  let styleOverride;
  if (event.type === ContentType.summit) {
    let t2;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        "--color-body-accent": "var(--color-summit-accent)",
        "--cds-color-selectedgraphics-background-primary": "var(--color-summit-accent)"
      };
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    styleOverride = t2 as CSSProperties;
  }
  const t2 = `h${initialHeadingLevel + 1}` as ElementType;
  let t3;
  if ($[11] !== event.subtype || $[12] !== event.type) {
    t3 = <ContentTypeHeader type={event.type} eventSubtype={event.subtype} />;
    $[11] = event.subtype;
    $[12] = event.type;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== event) {
    t4 = !event.isDateProvisional && <EventLiveIndicator event={event} />;
    $[14] = event;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t3 || $[17] !== t4) {
    t5 = <BadgeList>{t3}{t4}</BadgeList>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== event) {
    t6 = <CardBody><EventMetadata className="cds-t-body" event={event} /></CardBody>;
    $[19] = event;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== event) {
    t7 = <CardActions><EventCTA event={event} /></CardActions>;
    $[21] = event;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== className || $[24] !== event.url || $[25] !== rest || $[26] !== styleOverride || $[27] !== t2 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7 || $[31] !== title || $[32] !== titleId) {
    t8 = <Card id={titleId} href={event.url} className={className} title={title} headingLevel={t2} {...rest} prefix={t5} style={styleOverride}>{t6}{t7}</Card>;
    $[23] = className;
    $[24] = event.url;
    $[25] = rest;
    $[26] = styleOverride;
    $[27] = t2;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = title;
    $[32] = titleId;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  return t8;
};
const PublicationEvent: FCl<IProps & {
  event: IEventPublication;
}> = t0 => {
  const $ = _c(31);
  let className;
  let event;
  let initialHeadingLevel;
  let rest;
  if ($[0] !== t0) {
    ({
      className,
      event,
      initialHeadingLevel,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = event;
    $[3] = initialHeadingLevel;
    $[4] = rest;
  } else {
    className = $[1];
    event = $[2];
    initialHeadingLevel = $[3];
    rest = $[4];
  }
  const {
    t,
    lang
  } = useTranslation("common");
  const titleId = useId();
  const t1 = event.series ? `/series/${event.series.id}` : undefined;
  const t2 = event.title;
  const t3 = `h${initialHeadingLevel + 1}` as ElementType;
  let t4;
  if ($[5] !== event.type) {
    t4 = <ContentTypeHeader type={event.type} />;
    $[5] = event.type;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <dt><Icon use={<Calendar />} size={IconSize.SMALL} title="Date" /><Hidden>Date</Hidden></dt>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== event.startDate || $[9] !== lang) {
    t6 = getMonthAndYearStringForLocale(new Date(event.startDate), lang);
    $[8] = event.startDate;
    $[9] = lang;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <br />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = t("Event.subjectToChange");
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6 || $[15] !== t8) {
    t9 = <CardBody><StyledMetadataList className="cds-t-body"><div>{t5}<dd>{t6}* {t7}{t8}</dd></div></StyledMetadataList></CardBody>;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== event.series) {
    t10 = event.series && !event.series.isArchived && <SubscribeButton series={event.series} initialSubscriptionState={event.series.isSubscribed} />;
    $[17] = event.series;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10) {
    t11 = <CardActions>{t10}</CardActions>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== className || $[22] !== event.title || $[23] !== rest || $[24] !== t1 || $[25] !== t11 || $[26] !== t3 || $[27] !== t4 || $[28] !== t9 || $[29] !== titleId) {
    t12 = <Card id={titleId} href={t1} className={className} title={t2} headingLevel={t3} {...rest} prefix={t4}>{t9}{t11}</Card>;
    $[21] = className;
    $[22] = event.title;
    $[23] = rest;
    $[24] = t1;
    $[25] = t11;
    $[26] = t3;
    $[27] = t4;
    $[28] = t9;
    $[29] = titleId;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
};